import React, { useState, useEffect, useRef } from "react";
import { AiOutlineClose } from "react-icons/ai"; // Importamos el ícono de cierre
import styles from "./SelectSearch.module.css";

/**
 * Componente SelectSearch
 * Muestra un input con búsqueda y un listado de opciones parecido a un select.
 * No depende de InputQ, pero utiliza estilos propios.
 *
 * @param {string} label - Etiqueta visible al lado (o encima) del input.
 * @param {string} idProp - Nombre de la propiedad única que identifica cada opción (ej. "id", "materialId", etc.).
 * @param {string} labelProp - Nombre de la propiedad que muestra el texto (ej. "nombre", "title", etc.).
 * @param {Array} options - Arreglo de objetos que contienen idProp y labelProp.
 * @param {string} placeholder - Texto para el input si no hay valor.
 * @param {function} onOptionSelect - Función que se llama al seleccionar una opción.
 * @param {boolean} disabled - Deshabilita el input si es true.
 * @param {string} errorMessage - Mensaje de error opcional.
 */
function SelectSearch({
  label,
  idProp = "id",
  labelProp = "name",
  options = [],
  placeholder = "Buscar...",
  onOptionSelect = () => {},
  disabled = false,
  errorMessage = "",
  containerWidth= "100%"
}) {
  const [searchValue, setSearchValue] = useState("");
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showOptions, setShowOptions] = useState(false);

  const containerRef = useRef(null);

  useEffect(() => {
    // Cierra el menú si se hace clic fuera
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowOptions(false);
        setSelectedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filterList = (value) => {
    const filtered = options.filter((option) => {
      const textToCheck = option[labelProp] || "";
      return textToCheck.toLowerCase().includes(value.toLowerCase());
    });
    setFilteredOptions(filtered);
    setSelectedIndex(-1);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    filterList(value);
    setShowOptions(true);
  };

  const handleSelectOption = (option) => {
    setSearchValue(option[labelProp] || "");
    onOptionSelect(option);
    setShowOptions(false);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!showOptions) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) =>
        Math.min(prev + 1, filteredOptions.length - 1)
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (selectedIndex >= 0 && selectedIndex < filteredOptions.length) {
        handleSelectOption(filteredOptions[selectedIndex]);
      }
    } else if (e.key === "Escape") {
      setShowOptions(false);
      setSelectedIndex(-1);
    }
  };

  const handleFocus = () => {
    if (searchValue) {
      filterList(searchValue);
    } else {
      setFilteredOptions(options);
    }
    setShowOptions(true);
  };

  const handleToggleOptions = () => {
    if (disabled) return;
    if (showOptions) {
      setShowOptions(false);
      setSelectedIndex(-1);
    } else {
      handleFocus();
    }
  };

  const handleClear = () => {
    setSearchValue("");
    setFilteredOptions(options);
    setSelectedIndex(-1);
    setShowOptions(false);
    onOptionSelect(null);
  };

  return (
    <div className={styles.inputContainer} ref={containerRef}>
      <div className={styles.inputWrapper}>
        {label && <label className={styles.label}>{label}</label>}
        <div className={styles.inputFieldContainer}>
          <div className={styles.selectBox} style={{ width: containerWidth}}>
            <input
              type="text"
              className={styles.inputField}
              placeholder={placeholder}
              disabled={disabled}
              value={searchValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
            />

            {/* Ícono para togglear opciones (flecha) */}
            <div
              className={styles.arrowIcon}
              onClick={handleToggleOptions}
              role="button"
              tabIndex={0}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 320 512"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M143 352.3c-8.5 9.4-23.3 9.4-31.8 0l-112-128C-9.6 213.2 3 192 24 192h272c21 0 33.6 21.2 24.8 32.3l-112 128z" />
              </svg>
            </div>

            {/* Ícono para limpiar el input (solo se muestra si hay texto) */}
            {searchValue && (
              <div
                className={styles.clearIcon}
                onClick={handleClear}
                role="button"
                tabIndex={0}
              >
                <AiOutlineClose style={{cursor: "pointer"}}/>
              </div>
            )}
          </div>

          {/* Mensaje de error */}
          {errorMessage && (
            <span className={styles.errorMessage}>{errorMessage}</span>
          )}
        </div>
      </div>

      {/* Opciones desplegadas */}
      {showOptions && filteredOptions.length > 0 && (
        <div className={styles.optionsContainer}>
          {filteredOptions.map((option, index) => (
            <div
              key={option[idProp] ?? index}
              className={`${styles.optionItem} ${
                selectedIndex === index ? styles.selected : ""
              }`}
              onMouseDown={() => handleSelectOption(option)}
            >
              {option[labelProp]}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SelectSearch;
